import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { FaCalendarAlt, FaCheckCircle, FaEdit, FaPlus, FaTimes, FaStethoscope, FaHeartbeat, FaShieldAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import LoadingSpinner from '../common/LoadingSpinner';
import AppointmentEditModal from './AppointmentEditModal';
import './PostpartumSection.css';

function PostpartumAppointments() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState(null);
  const [suggestedAppointments, setSuggestedAppointments] = useState([]);
  const [userAppointments, setUserAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [isModifying, setIsModifying] = useState(false);

  // Generate auto-suggested appointments based on days postpartum
  const generateSuggestedAppointments = useCallback((daysPostpartum, profile) => {
    const suggestions = [];
    const weeks = Math.floor(daysPostpartum / 7);
    const months = Math.floor(daysPostpartum / 30);
    
    // 2-week check-in (if applicable, especially for high-risk)
    if (daysPostpartum >= 14 && daysPostpartum <= 21) {
      suggestions.push({
        id: '2week-checkin',
        type: 'CHECK_IN',
        name: t('postpartum.appointments.suggested.2week', '2-Week Postpartum Check-In'),
        timeframe: t('postpartum.appointments.suggested.2week.timeframe', 'Within 3 weeks'),
        description: t('postpartum.appointments.suggested.2week.desc', 'Initial follow-up to assess physical recovery, check for complications, review mental health, and discuss contraception.'),
        priority: 'HIGH',
        icon: <FaStethoscope />
      });
    }
    
    // 6-week comprehensive visit
    if (weeks >= 5 && weeks <= 8) {
      suggestions.push({
        id: '6week-visit',
        type: 'COMPREHENSIVE',
        name: t('postpartum.appointments.suggested.6week', '6-Week Comprehensive Postpartum Visit'),
        timeframe: t('postpartum.appointments.suggested.6week.timeframe', '6-12 weeks'),
        description: t('postpartum.appointments.suggested.6week.desc', 'Complete physical examination, assess emotional well-being, review birth control options, discuss return to exercise, screen for postpartum depression.'),
        priority: 'HIGH',
        icon: <FaHeartbeat />
      });
    }
    
    // Mental health check reminder
    if (daysPostpartum >= 14) {
      suggestions.push({
        id: 'mental-health-check',
        type: 'MENTAL_HEALTH',
        name: t('postpartum.appointments.suggested.mentalHealth', 'Mental Health Check'),
        timeframe: t('postpartum.appointments.suggested.mentalHealth.timeframe', 'Ongoing'),
        description: t('postpartum.appointments.suggested.mentalHealth.desc', 'Regular check-ins for emotional well-being and postpartum depression screening.'),
        priority: 'MEDIUM',
        icon: <FaHeartbeat />
      });
    }
    
    // Pelvic floor therapy suggestion
    if (weeks >= 6 && weeks <= 12) {
      suggestions.push({
        id: 'pelvic-floor-therapy',
        type: 'THERAPY',
        name: t('postpartum.appointments.suggested.pelvicFloor', 'Pelvic Floor Therapy Consultation'),
        timeframe: t('postpartum.appointments.suggested.pelvicFloor.timeframe', '6-12 weeks'),
        description: t('postpartum.appointments.suggested.pelvicFloor.desc', 'Consider pelvic floor therapy to support recovery, especially if experiencing any concerns.'),
        priority: 'MEDIUM',
        icon: <FaShieldAlt />
      });
    }
    
    // Vaccinations (mom-specific)
    if (daysPostpartum >= 0) {
      suggestions.push({
        id: 'vaccinations',
        type: 'VACCINATION',
        name: t('postpartum.appointments.suggested.vaccinations', 'Postpartum Vaccinations'),
        timeframe: t('postpartum.appointments.suggested.vaccinations.timeframe', 'As recommended'),
        description: t('postpartum.appointments.suggested.vaccinations.desc', 'Discuss recommended vaccinations with your healthcare provider (Tdap, flu, COVID-19, etc.).'),
        priority: 'MEDIUM',
        icon: <FaShieldAlt />
      });
    }
    
    // 3-6 month follow-up
    if (months >= 3 && months <= 6) {
      suggestions.push({
        id: '3-6month-followup',
        type: 'FOLLOW_UP',
        name: t('postpartum.appointments.suggested.followup', '3-6 Month Follow-Up'),
        timeframe: t('postpartum.appointments.suggested.followup.timeframe', '3-6 months'),
        description: t('postpartum.appointments.suggested.followup.desc', 'Follow-up visit to assess ongoing recovery and review any concerns.'),
        priority: 'LOW',
        icon: <FaCalendarAlt />
      });
    }
    
    return suggestions;
  }, [t]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const profileData = await api.getPregnancyProfile(userId).catch(() => null);
      
      if (profileData?.deliveryDate) {
        const delivery = new Date(profileData.deliveryDate);
        const today = new Date();
        const diffTime = Math.abs(today - delivery);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // Load existing appointments data
        const appointmentsData = await api.getPostpartumAppointments(diffDays).catch(() => null);
        setAppointments(appointmentsData);
        
        // Generate auto-suggestions
        const suggestions = generateSuggestedAppointments(diffDays, profileData);
        setSuggestedAppointments(suggestions);
        
        // Load user's saved appointments
        try {
          const response = await api.getPostpartumUserAppointments(userId);
          const data = response?.data || response;
          if (data && data.appointments) {
            setUserAppointments(data.appointments);
          }
        } catch (error) {
          console.error('Error loading user appointments:', error);
          // Fallback to localStorage if API fails
          const stored = localStorage.getItem(`postpartum_user_appointments_${userId}`);
          if (stored) {
            setUserAppointments(JSON.parse(stored));
          }
        }
      }
    } catch (error) {
      console.error('Error loading postpartum appointments:', error);
      setError(t('postpartum.error.load', 'Unable to load postpartum data. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [userId, t, generateSuggestedAppointments]);

  useEffect(() => {
    loadData();
  }, [userId, loadData]);

  if (loading) {
    return (
      <div className="postpartum-section">
        <LoadingSpinner message={t('postpartum.loading.appointments', 'Loading appointments information...')} />
      </div>
    );
  }

  return (
    <div className="postpartum-section">
      <div className="section-header">
        <button className="btn-back" onClick={() => navigate(`/postpartum/${userId}`)}>
          ← {t('action.back', 'Back')}
        </button>
        <h1>📅 {t('postpartum.title.appointments', 'Postpartum Appointments')}</h1>
        <nav className="postpartum-nav">
          <Link to={`/postpartum/${userId}/overview`} className="nav-link">{t('postpartum.nav.overview', 'Overview')}</Link>
          <Link to={`/postpartum/${userId}/recovery`} className="nav-link">{t('postpartum.nav.recovery', 'Recovery')}</Link>
          <Link to={`/postpartum/${userId}/nutrition`} className="nav-link">{t('postpartum.nav.nutrition', 'Nutrition')}</Link>
          <Link to={`/postpartum/${userId}/appointments`} className="nav-link active">{t('postpartum.nav.appointments', 'Appointments')}</Link>
          <Link to={`/postpartum/${userId}/warnings`} className="nav-link">{t('postpartum.nav.warnings', 'Warnings')}</Link>
        </nav>
      </div>

      <div className="section-content">
        {error && <div className="postpartum-error">{error}</div>}
        
        {/* Auto-Suggested Appointments */}
        {suggestedAppointments.length > 0 && (
          <section className="data-section suggested-appointments-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2>✨ {t('postpartum.appointments.suggested.title', 'Smart Appointment Suggestions')}</h2>
              <button
                className="add-custom-button"
                onClick={() => setShowAddModal(true)}
                style={{ padding: '8px 16px', background: '#E91E63', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <FaPlus /> {t('postpartum.appointments.addCustom', 'Add Custom')}
              </button>
            </div>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              {t('postpartum.appointments.suggested.subtitle', 'Zemuu auto-suggests appointments based on your recovery stage. Accept, modify, or add your own.')}
            </p>
            <div className="suggested-appointments-grid">
              {suggestedAppointments.map((suggestion) => {
                const isAccepted = userAppointments.some(ua => ua.suggestionId === suggestion.id);
                return (
                  <motion.div
                    key={suggestion.id}
                    className={`suggested-appointment-card priority-${suggestion.priority.toLowerCase()}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02, y: -4 }}
                  >
                    <div className="suggestion-header">
                      <div className="suggestion-icon" style={{ color: suggestion.priority === 'HIGH' ? '#F44336' : suggestion.priority === 'MEDIUM' ? '#FF9800' : '#4CAF50' }}>
                        {suggestion.icon}
                      </div>
                      <div className="suggestion-content">
                        <h3>{suggestion.name}</h3>
                        <p className="suggestion-timeframe">{suggestion.timeframe}</p>
                        <p className="suggestion-description">{suggestion.description}</p>
                      </div>
                    </div>
                    <div className="suggestion-actions">
                      {isAccepted ? (
                        <div className="accepted-badge">
                          <FaCheckCircle /> {t('postpartum.appointments.accepted', 'Accepted')}
                        </div>
                      ) : (
                        <>
                          <button
                            className="accept-button"
                            onClick={async () => {
                              try {
                                const appointmentData = {
                                  suggestionId: suggestion.id,
                                  name: suggestion.name,
                                  type: suggestion.type,
                                  timeframe: suggestion.timeframe,
                                  description: suggestion.description,
                                  priority: suggestion.priority,
                                  accepted: true,
                                  custom: false
                                };
                                await api.savePostpartumUserAppointment(userId, appointmentData);
                                
                                // Reload appointments
                                const response = await api.getPostpartumUserAppointments(userId);
                                const data = response?.data || response;
                                if (data && data.appointments) {
                                  setUserAppointments(data.appointments);
                                }
                              } catch (error) {
                                console.error('Error accepting appointment:', error);
                                // Fallback to localStorage
                                const newAppt = { ...suggestion, accepted: true, date: null };
                                const updated = [...userAppointments, newAppt];
                                setUserAppointments(updated);
                                localStorage.setItem(`postpartum_user_appointments_${userId}`, JSON.stringify(updated));
                              }
                            }}
                          >
                            <FaCheckCircle /> {t('postpartum.appointments.accept', 'Accept')}
                          </button>
                          <button
                            className="modify-button"
                            onClick={() => {
                              setIsModifying(true);
                              setEditingAppointment(suggestion);
                            }}
                          >
                            <FaEdit /> {t('postpartum.appointments.modify', 'Modify')}
                          </button>
                        </>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* User's Accepted Appointments */}
        {userAppointments.length > 0 && (
          <section className="data-section user-appointments-section">
            <h2>📅 {t('postpartum.appointments.yourAppointments', 'Your Appointments')}</h2>
            <div className="user-appointments-list">
              {userAppointments.map((appt, index) => (
                <div key={index} className="user-appointment-item">
                  <div className="appointment-info">
                    <h3>{appt.name}</h3>
                    <p>{appt.timeframe}</p>
                    {appt.date && <p><strong>{t('postpartum.appointments.scheduled', 'Scheduled')}:</strong> {new Date(appt.date).toLocaleDateString()}</p>}
                  </div>
                  <div className="appointment-actions">
                    <button className="edit-appointment-button" onClick={() => {
                      setIsModifying(false);
                      setEditingAppointment(appt);
                    }}>
                      <FaEdit />
                    </button>
                    <button
                      className="remove-appointment-button"
                      onClick={async () => {
                        try {
                          if (appt.id) {
                            await api.deletePostpartumUserAppointment(appt.id);
                            
                            // Reload appointments
                            const response = await api.getPostpartumUserAppointments(userId);
                            const data = response?.data || response;
                            if (data && data.appointments) {
                              setUserAppointments(data.appointments);
                            }
                          } else {
                            // No ID - remove from local state
                            const updated = userAppointments.filter((_, i) => i !== index);
                            setUserAppointments(updated);
                            localStorage.setItem(`postpartum_user_appointments_${userId}`, JSON.stringify(updated));
                          }
                        } catch (error) {
                          console.error('Error deleting appointment:', error);
                          // Fallback to localStorage
                          const updated = userAppointments.filter((_, i) => i !== index);
                          setUserAppointments(updated);
                          localStorage.setItem(`postpartum_user_appointments_${userId}`, JSON.stringify(updated));
                        }
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Existing Appointments Data */}
        {appointments && (
          <>
            {appointments.currentAppointment && (
              <section className="data-section current-appointment-section">
                <h2>📅 {t('postpartum.current.appointment', 'Current Appointment')}</h2>
                <div className="nutrient-foods-grid">
                  <div className="nutrient-category nutrient-category-info">
                    <h3>📋 {appointments.currentAppointment.name}</h3>
                    <p style={{marginBottom: '10px', fontWeight: 'bold'}}>
                      {appointments.currentAppointment.timeframe}
                    </p>
                    {appointments.currentAppointment.description && (
                      <p style={{marginBottom: '10px'}}>{appointments.currentAppointment.description}</p>
                    )}
                    {appointments.currentAppointment.includes && appointments.currentAppointment.includes.length > 0 && (
                      <div>
                        <strong style={{display: 'block', marginBottom: '8px', fontSize: '0.95rem'}}>This visit includes:</strong>
                        <ul>
                          {appointments.currentAppointment.includes.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {appointments.upcoming && appointments.upcoming.length > 0 && (
              <section className="data-section upcoming-appointments-section">
                <h2>🔜 {t('postpartum.upcoming.appointments', 'Upcoming Appointments')}</h2>
                <div className="nutrient-foods-grid">
                  <div className="nutrient-category">
                    <h3>📅 {t('postpartum.next.appointments', 'Next Appointments')}</h3>
                    {appointments.upcoming.map((appt, i) => (
                      <div key={i} style={{marginBottom: '15px', paddingBottom: '15px', borderBottom: i < appointments.upcoming.length - 1 ? '1px solid #e0e0e0' : 'none'}}>
                        <strong style={{display: 'block', marginBottom: '5px'}}>{appt.timeframe}: {appt.name}</strong>
                        {appt.description && (
                          <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '5px'}}>{appt.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {appointments.timeline && appointments.timeline.length > 0 && (
              <section className="data-section timeline-section">
                <h2>📊 {t('postpartum.appointment.timeline', 'Appointment Timeline')}</h2>
                <div className="nutrient-foods-grid">
                  <div className="nutrient-category">
                    <h3>📅 {t('postpartum.schedule', 'Postpartum Visit Schedule')}</h3>
                    {appointments.timeline.map((period, i) => (
                      <div key={i} style={{marginBottom: '15px'}}>
                        <strong style={{display: 'block', marginBottom: '5px'}}>{period.timeframe}</strong>
                        <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '5px'}}>
                          <strong>{period.frequency}:</strong> {period.description}
                        </p>
                        {period.includes && period.includes.length > 0 && (
                          <ul style={{fontSize: '0.9rem', color: '#666', marginTop: '5px'}}>
                            {period.includes.map((item, j) => (
                              <li key={j}>{item}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Appointment Modal */}
      <AppointmentEditModal
        isOpen={showAddModal || !!editingAppointment}
        onClose={() => {
          setShowAddModal(false);
          setEditingAppointment(null);
          setIsModifying(false);
        }}
        onSave={async (appointmentData) => {
          try {
            if (editingAppointment?.id && !isModifying) {
              // Update existing appointment - need to delete old and create new
              // Since backend doesn't have update endpoint, we'll delete and recreate
              if (editingAppointment.id) {
                await api.deletePostpartumUserAppointment(editingAppointment.id);
              }
            }
            
            // Save the appointment (new or modified)
            await api.savePostpartumUserAppointment(userId, appointmentData);
            
            // Reload appointments
            const response = await api.getPostpartumUserAppointments(userId);
            const data = response?.data || response;
            if (data && data.appointments) {
              setUserAppointments(data.appointments);
            }
            
            setShowAddModal(false);
            setEditingAppointment(null);
            setIsModifying(false);
          } catch (error) {
            console.error('Error saving appointment:', error);
            setError(t('postpartum.appointments.error.save', 'Failed to save appointment. Please try again.'));
            // Fallback to localStorage
            if (editingAppointment?.id && !isModifying) {
              const updated = userAppointments.filter(apt => apt.id !== editingAppointment.id);
              updated.push({ ...appointmentData, id: editingAppointment.id });
              setUserAppointments(updated);
              localStorage.setItem(`postpartum_user_appointments_${userId}`, JSON.stringify(updated));
            } else {
              const updated = [...userAppointments, { ...appointmentData, id: Date.now() }];
              setUserAppointments(updated);
              localStorage.setItem(`postpartum_user_appointments_${userId}`, JSON.stringify(updated));
            }
            setShowAddModal(false);
            setEditingAppointment(null);
            setIsModifying(false);
          }
        }}
        appointment={editingAppointment}
        isModify={isModifying}
      />
    </div>
  );
}

export default PostpartumAppointments;

